import { useState } from "react";
import { toast } from "@fox1ck-org/ui/toast";
import { useCreateOrgUnit } from "@/hooks/use-org-units";
import { useOrganizations } from "@/hooks/use-organizations";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@fox1ck-org/ui";
import type { OrgUnit, OrgUnitType } from "@/types/org";

interface CreateOrgUnitDialogProps {
  tree: OrgUnit[];
  onClose: () => void;
}

function flattenTree(nodes: OrgUnit[]): { id: string; name: string; type: OrgUnitType; depth: number; keycloakOrgId: string | null }[] {
  const result: { id: string; name: string; type: OrgUnitType; depth: number; keycloakOrgId: string | null }[] = [];
  function walk(list: OrgUnit[], depth: number) {
    for (const node of list) {
      result.push({ id: node.id, name: node.name, type: node.type, depth, keycloakOrgId: node.keycloakOrgId });
      if (node.children) walk(node.children, depth + 1);
    }
  }
  walk(nodes, 0);
  return result;
}

export function CreateOrgUnitDialog({ tree, onClose }: CreateOrgUnitDialogProps) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [type, setType] = useState<OrgUnitType>("team");
  const [parentId, setParentId] = useState<string>("");
  const [orgId, setOrgId] = useState<string>("");
  const createUnit = useCreateOrgUnit();
  const { data: orgsData } = useOrganizations({ max: 100 });

  const orgs = orgsData?.organizations ?? [];

  const autoSlug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

  const flatNodes = flattenTree(tree);

  const parentOptions = flatNodes.filter((n) => {
    if (type === "company") return false;
    if (type === "department") return n.type === "company";
    return n.type === "company" || n.type === "department";
  });

  const isCompany = type === "company";

  // For department/team, resolve organization from parent
  const selectedParent = flatNodes.find((n) => n.id === parentId);
  const resolvedOrgName = isCompany
    ? orgs.find((o) => o.id === orgId)?.name
    : selectedParent?.keycloakOrgId
      ? orgs.find((o) => o.id === selectedParent.keycloakOrgId)?.name
      : null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    await createUnit.mutateAsync(
      {
        name,
        slug: slug || autoSlug,
        type,
        parentId: isCompany ? undefined : parentId || undefined,
        keycloakOrgId: isCompany ? orgId || undefined : undefined,
      },
      {
        onSuccess: () => {
          toast.success("Подразделение создано");
          onClose();
        },
        onError: (err) => toast.error(`Не удалось создать подразделение: ${err.message}`),
      }
    );
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Новое подразделение</DialogTitle>
          <DialogDescription>Добавить подразделение в оргструктуру.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">Название</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="например, Разработка"
            />
          </div>
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">Код</label>
            <Input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder={autoSlug || "auto-generated"}
              className="font-mono"
            />
          </div>
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">Тип</label>
            <Select value={type} onValueChange={(v) => { setType(v as OrgUnitType); setParentId(""); setOrgId(""); }}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="company">Компания</SelectItem>
                <SelectItem value="department">Отдел</SelectItem>
                <SelectItem value="team">Команда</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {isCompany && (
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Организация</label>
              <Select value={orgId} onValueChange={setOrgId}>
                <SelectTrigger>
                  <SelectValue placeholder="Выберите организацию" />
                </SelectTrigger>
                <SelectContent>
                  {orgs.length === 0 ? (
                    <SelectItem value="__none" disabled>
                      Нет доступных организаций
                    </SelectItem>
                  ) : (
                    orgs.map((org) => (
                      <SelectItem key={org.id} value={String(org.id)}>
                        {org.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Связывает компанию с организацией в Keycloak.
              </p>
            </div>
          )}
          {!isCompany && (
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Родитель</label>
              <Select value={parentId} onValueChange={setParentId}>
                <SelectTrigger>
                  <SelectValue placeholder="Выберите родителя" />
                </SelectTrigger>
                <SelectContent>
                  {parentOptions.length === 0 ? (
                    <SelectItem value="__none" disabled>
                      Нет подходящих родителей
                    </SelectItem>
                  ) : (
                    parentOptions.map((opt) => (
                      <SelectItem key={opt.id} value={String(opt.id)}>
                        {"  ".repeat(opt.depth)}{opt.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {resolvedOrgName && (
                <p className="text-xs text-muted-foreground mt-1">
                  Организация: <span className="font-medium text-foreground">{resolvedOrgName}</span> (от родителя)
                </p>
              )}
            </div>
          )}
          {createUnit.isError && (
            <p className="text-sm text-destructive">{createUnit.error.message}</p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Отмена
            </Button>
            <Button
              type="submit"
              disabled={!name || (!isCompany && !parentId) || createUnit.isPending}
            >
              {createUnit.isPending ? "Создаём…" : "Создать"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
